import { T, Var, useGT } from 'gt-react';
import { format } from 'date-fns';
import type { InvoiceEvent, TaskTimelineEntry } from '@erp/rpc';
import { NoteRow } from '@/components/common/comments/NoteRow';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatLogTaskStatus } from '@/components/accounts-receivable/cash-receipt-detail/cashReceiptTaskFormat';
import type { ArTaskFeedEntry } from './arTaskFeed';
import { ErpChip } from './ErpChip';

type Gt = ReturnType<typeof useGT>;

/** The viewer and names the task's own notes need. */
export interface ArTaskFeedPeople {
  currentUserId: string | undefined;
  userNameById: Map<string, string>;
  onUpdate: (commentId: string, body: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
}

const EVENT_ROW_CLASS =
  'flex min-w-0 items-baseline gap-2 px-2 py-1 text-[11px] text-muted-foreground';

/**
 * "×6" after a row that repeated back to back; hovering explains that only
 * the newest of those repeats is shown.
 */
function RepeatCount({
  repeats,
  what,
}: {
  repeats: number | undefined;
  what: 'note' | 'event';
}) {
  if (!repeats || repeats < 2) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-default rounded bg-stone-200 px-1 text-[9px] font-semibold text-stone-600 tabular-nums">
          <T context="AR task feed, how many times a note or event repeated back to back; only the newest is shown">
            ×<Var>{repeats}</Var>
          </T>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {what === 'note' ? (
          <T context="AR task feed, tooltip on the repeat count of an ERP note">
            This note was added <Var>{repeats}</Var> times in a row. Only the
            newest is shown.
          </T>
        ) : (
          <T context="AR task feed, tooltip on the repeat count of an invoice event">
            This happened <Var>{repeats}</Var> times in a row. Only the newest
            is shown.
          </T>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function FeedDate({ at }: { at: Date }) {
  return (
    <time
      className="ml-auto shrink-0 tabular-nums"
      dateTime={at.toISOString()}
      title={format(at, 'MMM d, yyyy · h:mm a')}
    >
      {format(at, 'MMM d')}
    </time>
  );
}

/** A note the ERP or the order owns: read-only, marked with its source. */
function ErpNoteRow({
  entry,
}: {
  entry: Extract<ArTaskFeedEntry, { kind: 'erp_note' }>;
}) {
  return (
    <li className="rounded-md border border-dashed bg-stone-50 px-2 py-1.5">
      <div className="flex items-baseline gap-2 text-[10px] text-muted-foreground">
        {entry.source === 'invoice' ? (
          <ErpChip />
        ) : (
          <span className="shrink-0 rounded bg-stone-200 px-1 text-[9px] font-semibold tracking-wide text-stone-600 uppercase">
            <T context="chip on a note carried on the sales order, which can't be edited here">
              Sales order
            </T>
          </span>
        )}
        <RepeatCount repeats={entry.repeats} what="note" />
        {!entry.undated && <FeedDate at={entry.at} />}
      </div>
      <p className="m-0 mt-0.5 text-xs leading-snug whitespace-pre-wrap text-stone-800">
        {entry.body}
      </p>
    </li>
  );
}

function invoiceEventLabels(gt: Gt): Partial<Record<string, string>> {
  const context = 'AR task feed, an event on the task invoice';
  return {
    invoice_sent: gt('Invoice sent', { context }),
    invoice_opened: gt('Invoice synced from the ERP', { context }),
    remittance_received: gt('Remittance received', { context }),
    payment_received: gt('Payment received', { context }),
  };
}

/** Any other invoice event, as one line with its note. */
function RecordEventRow({
  event,
  at,
  repeats,
}: {
  event: InvoiceEvent;
  at: Date;
  repeats: number | undefined;
}) {
  const gt = useGT();
  const label =
    invoiceEventLabels(gt)[event.eventType] ??
    event.eventType.replace(/_/g, ' ');
  return (
    <li className={EVENT_ROW_CLASS}>
      <span className="shrink-0 font-medium text-stone-700">{label}</span>
      <span className="truncate" title={event.note ?? undefined}>
        {event.note}
      </span>
      <RepeatCount repeats={repeats} what="event" />
      <FeedDate at={at} />
    </li>
  );
}

function TaskEventText({ entry }: { entry: TaskTimelineEntry }) {
  const gt = useGT();
  switch (entry.kind) {
    case 'task_created':
      return <T context="AR task feed, the task was opened">Task opened</T>;
    case 'task_status_changed':
    case 'task_concluded':
      return (
        <T context="AR task feed, the task's status changed">
          Status changed to <Var>{formatLogTaskStatus(entry.status, gt)}</Var>
        </T>
      );
    default:
      return (
        <T context="AR task feed, the task's assignee changed">
          Assignee changed
        </T>
      );
  }
}

/** A close's outcome and note, when the entry carries them. */
function closeDetails(
  entry: TaskTimelineEntry,
): { outcome: string | null; reason: string | null } | null {
  if (entry.kind !== 'task_status_changed' && entry.kind !== 'task_concluded') {
    return null;
  }
  const outcome = entry.outcome?.replace(/_/g, ' ') ?? null;
  const reason = entry.outcomeReason?.trim() || null;
  return outcome || reason ? { outcome, reason } : null;
}

function CloseDetails({
  outcome,
  reason,
}: {
  outcome: string | null;
  reason: string | null;
}) {
  return (
    <span className="flex min-w-0 flex-col pl-0.5 text-stone-600">
      {outcome && (
        <span>
          <T context="AR task feed, the outcome chosen when the task was closed">
            Outcome:{' '}
            <Var>
              <span className="capitalize">{outcome}</span>
            </Var>
          </T>
        </span>
      )}
      {reason && <span className="whitespace-pre-wrap">{reason}</span>}
    </span>
  );
}

function TaskEventRow({ entry, at }: { entry: TaskTimelineEntry; at: Date }) {
  const details = closeDetails(entry);
  const line = (
    <>
      <span className="truncate">
        <TaskEventText entry={entry} />
      </span>
      <FeedDate at={at} />
    </>
  );
  if (!details) return <li className={EVENT_ROW_CLASS}>{line}</li>;
  return (
    <li className="flex min-w-0 flex-col gap-0.5">
      <div className={EVENT_ROW_CLASS}>{line}</div>
      <div className="px-2 pb-1 text-xs">
        <CloseDetails {...details} />
      </div>
    </li>
  );
}

/** One feed entry, drawn by its kind. */
export function ArTaskFeedRow({
  entry,
  people,
}: {
  entry: ArTaskFeedEntry;
  people: ArTaskFeedPeople;
}) {
  switch (entry.kind) {
    case 'comment':
      return <NoteRow comment={entry.comment} {...people} />;
    case 'erp_note':
      return <ErpNoteRow entry={entry} />;
    case 'record_event':
      return (
        <RecordEventRow
          event={entry.event}
          at={entry.at}
          repeats={entry.repeats}
        />
      );
    default:
      return <TaskEventRow entry={entry.entry} at={entry.at} />;
  }
}
